import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const AuthContext = createContext(null);

// Capture ?ref=CODE from URL on first load and persist for use after sign-in
const captureReferralFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && !localStorage.getItem("pending_referral")) {
      localStorage.setItem("pending_referral", ref.trim().toUpperCase());
    }
  } catch {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Retry once with backoff so a transient 502/network blip doesn't
    // briefly flip user→null and trigger a visible sign-out/sign-in
    // flicker across the whole app.
    let data;
    let lastErr;
    for (let i = 0; i < 2; i++) {
      try {
        const res = await api.get("/auth/me");
        data = res.data;
        lastErr = null;
        break;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // 401 = genuine "not signed in" → bail immediately, no retry.
        if (status === 401) break;
        if (i === 0) {
          await new Promise((r) => setTimeout(r, 700));
        }
      }
    }
    if (lastErr) {
      const status = lastErr?.response?.status;
      if (status === 401) {
        // Real auth failure — clear user state.
        setUser(null);
      } else {
        // Network blip, 5xx, CORS hiccup, etc. KEEP existing user state.
        // Setting user=null here was the platform-wide "signs me out
        // and signs me back in" bug — the cookie is still valid, just
        // the request couldn't reach the backend cleanly.
        console.warn("Auth check transient failure (keeping current user):", status, lastErr?.message);
      }
      setLoading(false);
      return;
    }
    try {
      setUser(data);
      if (data?.daily_bonus?.credited) {
        const { amount, streak_days } = data.daily_bonus;
        const headline = streak_days > 1
          ? `🔥 ${streak_days}-day streak! ₹${amount} added to your wallet`
          : `Daily login bonus: ₹${amount} added to your wallet ✨`;
        toast.success(headline, { duration: 5000 });
      }
      // One-shot: VIP unlock-all window just ended — tell the user, then ack
      if (data?.vip_just_expired) {
        toast(
          "👑 Your VIP King unlock-all window has ended. Premium tools are back behind their individual paywall — re-up to lock in another 7 days.",
          { duration: 9000 },
        );
        // Fire-and-forget ack so we don't keep nagging
        api.post("/vip/ack-expiry").catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Capture referral code from URL once on mount
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // After user is authenticated, apply any pending referral once
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem("pending_referral");
    if (!pending) return;
    localStorage.removeItem("pending_referral"); // prevent retry loops
    api.post("/customer/apply-referral", { code: pending })
      .then(({ data }) => {
        toast.success(`Referral bonus: ₹${data.amount} added — courtesy of ${data.referrer_name} ✨`, { duration: 6000 });
        checkAuth(); // refresh balance + referred_by state
      })
      .catch((err) => {
        // Silent: most common case is "already used" for returning users
        console.warn("Referral apply skipped:", err?.response?.data?.detail);
      });
  }, [user, checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout failed", err);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout }),
    [user, loading, checkAuth, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
